/**
 * User model for mobile client
 * Represents the user data structure used in the mobile application
 */
import {defaultUserController} from "../controllers/UserController";
import Profile from "./Profile";
import Settings from "./Settings";
import { images } from "../modules/Resources";

class User {
  constructor({
    id = null,
    username = '',
    email = '',
    password = '',
    role = 'user',
    status = 'active',
    providerId = null,
    provider = 'email',
    lastOnline = null,
    isPremium = false,
    isEmailVerified = false,
    isPhoneVerified = false,
    phoneNumber = null,
    resetToken = null,
    resetTokenExpiry = null,
    tokenVersion = 0,
  } = {}) {
    this.id = id;
    this.username = username;
    this.email = email;
    this.password = password;
    this.role = role;
    this.status = status;
    this.providerId = providerId;
    this.provider = provider;
    this.lastOnline = lastOnline ? new Date(lastOnline) : new Date();
    this.isPremium = isPremium;
    this.isEmailVerified = isEmailVerified;
    this.isPhoneVerified = isPhoneVerified;
    this.phoneNumber = phoneNumber;
    this.resetToken = resetToken;
    this.resetTokenExpiry = resetTokenExpiry ? new Date(resetTokenExpiry) : null;
    this.tokenVersion = tokenVersion;

    this.profile  = null;
    this.is = new Is();
  }

  /**
   * Check if user has administrator role
   * @returns {boolean} True if user is an administrator
   */
  get isAdmin() {
    return this.role === 'administrator';
  }

  /**
   * Factory method to create a User instance from API response
   * @param {Object} data - Raw user data from API
   * @returns {User} - Instantiated User object
   */
  static fromAPI(data) {
    return new User({
      id: data.id,
      username: data.username,
      email: data.email,
      password: data.password,
      role: data.role,
      status: data.status,
      providerId: data.providerId,
      provider: data.provider,
      lastOnline: data.lastOnline,
      isPremium: data.isPremium,
      isEmailVerified: data.isEmailVerified,
      isPhoneVerified: data.isPhoneVerified,
      phoneNumber: data.phoneNumber,
      resetToken: data.resetToken,
      resetTokenExpiry: data.resetTokenExpiry,
      tokenVersion: data.tokenVersion,
    });
  }

  applyFromAPI(data) {
    if (!data) return;
    
    this.id = data.id;
    this.username = data.username;
    this.email = data.email;
    this.password = data.password;
    this.role = data.role;
    this.status = data.status;
    this.providerId = data.providerId;
    this.provider = data.provider;
    this.lastOnline = data.lastOnline;
    this.isPremium = data.isPremium;
    this.isEmailVerified = data.isEmailVerified;
    this.isPhoneVerified = data.isPhoneVerified;
    this.phoneNumber = data.phoneNumber;
    this.resetToken = data.resetToken;
    this.resetTokenExpiry = data.resetTokenExpiry;
    this.tokenVersion = data.tokenVersion;
  }

   static fromData(data, additional) {
    const NEWUSER = new User();
    const user = data.user;
    const profile = data.profile;

    NEWUSER.profile = Profile.fromAPI(profile);

    NEWUSER.applyFromAPI(user);  

    NEWUSER.settings = Settings.fromAPI(data.settings);

    NEWUSER.is = Is.fromData(additional);

    return NEWUSER;
  }

  static fromObject(data) {
    if (!data) return null;
    
    const NEWUSER = new User();
    
    NEWUSER.applyFromAPI(data);

    NEWUSER.profile = Profile.fromAPI(data.profile);

    NEWUSER.settings = Settings.fromAPI(data.settings);

    return NEWUSER;
  }


  /**
   * Convert User instance to plain object (for storage, etc.)
   * @returns {Object} Plain JavaScript object
   */
  toJSON() {
    return {
      id: this.id,
      username: this.username,
      email: this.email,
      password: this.password,
      role: this.role,
      status: this.status,
      providerId: this.providerId,
      provider: this.provider,
      lastOnline: this.lastOnline?.toISOString() || null,
      isPremium: this.isPremium,
      isEmailVerified: this.isEmailVerified,
      isPhoneVerified: this.isPhoneVerified,
      phoneNumber: this.phoneNumber,
      resetToken: this.resetToken,
      resetTokenExpiry: this.resetTokenExpiry ? this.resetTokenExpiry.toISOString() : null,
      tokenVersion: this.tokenVersion,
    };
  }

  update(data) {
    return defaultUserController.updateUser(this.id, data);
  }

  getDisplayName() {
    return this.profile?.displayName || this.username;
  }

  getAvatar() {
    return this.profile?.currentAvatar?.url ? { uri: this.profile.currentAvatar?.url } : images.default_avatar;
  }

  getBanner() {
    return this.profile?.currentBanner?.url ? { uri: this.profile.currentBanner?.url } : images.default_banner;
  }
}

class Is {
  constructor() {
    this.blocked = false;
    this.muted = false;
    this.reported = false;
    this.followed = false;
  }

  static fromData(data) {
    if (!data) return new Is();

    const NEWIS = new Is();

    NEWIS.blocked = data.blocked;
    NEWIS.muted = data.muted;
    NEWIS.reported = data.reported;
    NEWIS.followed = data.follow;

    return NEWIS;
  }
}

export default User;